import styled, { keyframes } from "styled-components";

import { pageRoot } from "../../styles/pageRoot";
import {
  actionListSubtitle,
  actionListTitle,
  actionTileTitle,
  countBadgeText,
  fontSizes,
  lineHeights,
  sectionTitle,
} from "../../shared/styles/typography";
import { tokens } from "../../theme/tokens";

const BP = tokens.layout.mobileFirstExpandPx;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const PageContainer = styled.div`
  ${pageRoot}
  background: ${({ theme }) => theme.colors.background};
  align-items: center;
`;

export const SectionTitle = styled.h3`
  ${sectionTitle}
  margin-top: 8px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

export const TitleBadge = styled.span`
  background: ${({ theme }) => theme.colors.info};
  color: ${({ theme }) => theme.colors.white};
  padding: 4px 12px;
  border-radius: ${({ theme }) => theme.borderRadius.lg}px;
  ${countBadgeText}
`;

export const HorizontalScroll = styled.div`
  display: flex;
  gap: 12px;
  overflow-x: auto;
  padding: 12px 0 28px;
  margin: 0 -16px;
  padding-left: 16px;
  padding-right: 16px;
  scrollbar-width: none;
  width: 100%;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (min-width: ${BP}px) {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    overflow: visible;
    margin: 0;
    padding: 16px 0 32px;
    gap: 8px;
  }
`;

/** actionTile — alinhado ao HomeActionCard mobile */
export const FeatureCard = styled.button`
  min-width: 280px;
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.borderRadius.xl}px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  padding: 24px 8px;
  min-height: 124px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-align: center;
  cursor: pointer;
  transition: transform 0.15s cubic-bezier(0.34, 1.56, 0.64, 1);

  &:active {
    transform: scale(0.96);
  }

  @media (min-width: ${BP}px) {
    min-width: 0;
    width: 100%;
  }
`;

export const FeatureHeader = styled.div`
  display: none;
`;

export const FeatureType = styled.span`
  display: none;
`;

export const FeatureIconBox = styled.div<{ $color: string }>`
  width: 56px;
  height: 56px;
  border-radius: 28px;
  background: ${(props) => `${props.$color}22`};
  color: ${(props) => props.$color};
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const FeatureName = styled.h4`
  ${actionTileTitle}
  color: ${({ theme }) => theme.colors.textTitle};
  margin: 0;
`;

export const ProgressWrapper = styled.div`
  display: none;
`;

export const ProgressFill = styled.div`
  display: none;
`;

/** WordOfTheDay — alinhado ao app mobile (info, radius xl) */
export const WordOfDayCard = styled.div`
  background: ${({ theme }) => theme.colors.info};
  border-radius: ${({ theme }) => theme.borderRadius.xl}px;
  border: 2px solid ${({ theme }) => theme.colors.info};
  padding: 16px;
  color: white;
  margin-top: 4px;
  margin-bottom: 12px;
  position: relative;
  overflow: hidden;
  width: 100%;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.02);
  animation: ${fadeUp} 0.5s ease-out;
  transition: transform 0.15s ease;

  &[role="button"] {
    cursor: pointer;

    &:focus-visible {
      outline: 2px solid #fff;
      outline-offset: 3px;
    }

    &:active {
      transform: scale(0.99);
      opacity: 0.95;
    }
  }
`;

export const WodBadgeRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

export const WodBadge = styled.div`
  background: rgba(255, 255, 255, 0.25);
  padding: 8px 12px;
  border-radius: ${({ theme }) => theme.borderRadius.xl}px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  font-family: ${({ theme }) => theme.font.familyHeading};
  font-size: ${fontSizes.sm}px;
  line-height: ${lineHeights.base}px;
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.5px;
  text-transform: uppercase;
`;

export const PlaybookIconBox = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.2);
  border: 2px solid rgba(255, 255, 255, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const WodTitle = styled.h2`
  font-family: ${({ theme }) => theme.font.familyHeading};
  font-size: ${fontSizes["2xl"]}px;
  line-height: ${lineHeights["2xl"]}px;
  font-weight: var(--font-weight-bold);
  margin-bottom: 8px;
  color: white;
  letter-spacing: 0.5px;
`;

export const WodDesc = styled.p`
  font-family: ${({ theme }) => theme.font.family};
  font-size: ${fontSizes.lg}px;
  line-height: ${lineHeights.lg}px;
  font-weight: var(--font-weight-body);
  opacity: 0.95;
  margin-bottom: 16px;
  max-width: 100%;
`;

export const WodFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
`;

export const WodButton = styled.button`
  background: rgba(255, 255, 255, 0.25);
  padding: 12px 24px;
  border-radius: ${({ theme }) => theme.borderRadius.full}px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  color: white;
  font-family: ${({ theme }) => theme.font.familyHeading};
  font-size: ${fontSizes.sm}px;
  line-height: ${lineHeights.sm}px;
  font-weight: var(--font-weight-semibold);
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  flex: 1;
  justify-content: center;
  min-height: 56px;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.35);
  }

  &:active {
    transform: scale(0.95);
  }
`;

export const HeartBtn = styled.button<{ $isFavorite?: boolean }>`
  width: 48px;
  height: 48px;
  min-width: 48px;
  min-height: 48px;
  border-radius: ${({ theme }) => theme.borderRadius.xxl}px;
  background: rgba(255, 255, 255, 0.25);
  border: 2px solid
    ${(props) =>
      props.$isFavorite
        ? props.theme.colors.danger
        : "rgba(255, 255, 255, 0.4)"};
  color: ${(props) =>
    props.$isFavorite ? props.theme.colors.danger : "white"};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:active {
    transform: scale(0.95);
    opacity: 0.8;
  }
`;

export const FocusCard = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.borderRadius.xl}px;
  padding: 20px;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 16px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  cursor: pointer;
  transition: transform 0.15s ease;

  @media (min-width: ${BP}px) {
    margin-bottom: 0;
  }

  &:active {
    transform: scale(0.99);
  }
`;

export const FocusIconBox = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${({ theme }) => theme.borderRadius.md}px;
  background: ${({ theme }) => theme.colors.primaryAlpha10};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.primary};
`;

export const FocusContent = styled.div`
  flex: 1;
`;

export const FocusTitle = styled.h4`
  font-family: ${({ theme }) => theme.font.family};
  font-size: ${fontSizes.sm}px;
  line-height: ${lineHeights.sm}px;
  font-weight: var(--font-weight-medium);
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 4px;
`;

export const FocusValue = styled.span`
  font-family: ${({ theme }) => theme.font.familyHeading};
  font-size: ${fontSizes["2xl"]}px;
  line-height: ${lineHeights["2xl"]}px;
  font-weight: var(--font-weight-bold);
  color: ${({ theme }) => theme.colors.textTitle};
`;

export const ListSection = styled.section`
  margin-bottom: 32px;
  animation: ${fadeUp} 0.7s ease-out;
  width: 100%;
`;

export const ListHeader = styled.h4`
  font-family: ${({ theme }) => theme.font.family};
  font-size: ${fontSizes.base}px;
  line-height: ${lineHeights.base}px;
  font-weight: var(--font-weight-medium);
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 8px;
`;

export const ListSubtext = styled.p`
  font-family: ${({ theme }) => theme.font.family};
  font-size: ${fontSizes.sm}px;
  line-height: ${lineHeights.sm}px;
  font-weight: var(--font-weight-body);
  color: ${({ theme }) => theme.colors.textTertiary};
  margin-bottom: 22px;
`;

export const ListItemsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  @media (min-width: ${BP}px) {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
  }

  @media (min-width: 1200px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

/** actionListCard — alinhado ao HomeActionCard list variant */
export const ListItem = styled.button`
  width: 100%;
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.borderRadius.xl}px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  padding: 16px;
  min-height: 76px;
  display: flex;
  align-items: center;
  gap: 16px;
  cursor: pointer;
  text-align: left;
  transition: transform 0.15s ease;

  &:active {
    transform: scale(0.98);
  }
`;

export const ListItemIconBox = styled.div<{ $color: string }>`
  width: 52px;
  height: 52px;
  border-radius: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) => `${props.$color}22`};
  color: ${(props) => props.$color};
  flex-shrink: 0;
`;

export const ListItemContent = styled.div`
  flex: 1;
  min-width: 0;
`;

export const ListItemTitle = styled.h3`
  ${actionListTitle}
  color: ${({ theme }) => theme.colors.textTitle};
  margin: 0 0 4px;
`;

export const ListItemSubtitle = styled.p`
  ${actionListSubtitle}
  margin: 0;
`;

export const FooterSection = styled.footer`
  background: ${({ theme }) => theme.colors.surface};
  padding: 28px 24px 32px;
  margin-top: 16px;
  margin-bottom: 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.xl}px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
  width: 100%;
`;

export const AboutLink = styled.button`
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.primary};
  font-family: ${({ theme }) => theme.font.familyHeading};
  font-size: ${fontSizes.sm}px;
  line-height: ${lineHeights.sm}px;
  font-weight: var(--font-weight-semibold);
  text-decoration: underline;
  cursor: pointer;
`;

export const SeloBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding-top: 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.borderLight};
  width: 100%;

  img {
    height: 50px;
    object-fit: contain;
  }

  span {
    font-family: ${({ theme }) => theme.font.family};
    font-size: ${fontSizes.sm}px;
    line-height: ${lineHeights.sm}px;
    color: ${({ theme }) => theme.colors.textTertiary};
    text-align: center;
  }
`;

export const SupportText = styled.p`
  text-align: center;
  font-family: ${({ theme }) => theme.font.family};
  font-size: ${fontSizes.sm}px;
  line-height: ${lineHeights.sm}px;
  color: ${({ theme }) => theme.colors.textTertiary};
  margin-top: 8px;
`;
